import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { loginWithCognito, extractUserFromToken, isTokenExpired } from '../services/authService';

const AuthContext = createContext(null);

/**
 * Local storage key for the ID token.
 * Note: In production, consider using httpOnly cookies. For this SPA,
 * localStorage is acceptable since no sensitive user data is stored.
 */
const TOKEN_KEY = 'kickereck_admin_token';
const EXPIRY_KEY = 'kickereck_admin_expiry';
const USER_KEY = 'kickereck_admin_user';

/**
 * AuthProvider manages Cognito authentication state for the admin area.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    /**
     * Clears auth state and localStorage.
     */
    const clearAuth = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EXPIRY_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
    }, []);

    // Restore session from localStorage on mount
    useEffect(() => {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedExpiry = localStorage.getItem(EXPIRY_KEY);
        const storedUser = localStorage.getItem(USER_KEY);

        if (storedToken && storedExpiry && storedUser) {
            const expiresAt = parseInt(storedExpiry, 10);
            if (!isTokenExpired(expiresAt)) {
                try {
                    setToken(storedToken);
                    setUser(JSON.parse(storedUser));
                } catch {
                    clearAuth();
                }
            } else {
                clearAuth();
            }
        }
        setLoading(false);
    }, [clearAuth]);

    /**
     * Authenticates with Cognito and stores the ID token.
     * Returns the login result including potential challenge.
     *
     * @param {string} email - User email
     * @param {string} password - User password
     * @returns {Promise<{success: boolean, challenge?: string, session?: string}>}
     */
    const login = useCallback(async (email, password) => {
        const result = await loginWithCognito(email, password);

        // Propagate NEW_PASSWORD_REQUIRED challenge to caller
        if (result.challenge) {
            return { success: false, challenge: result.challenge, session: result.session };
        }

        const userInfo = extractUserFromToken(result.idToken);

        if (!userInfo) {
            throw new Error('Token konnte nicht verarbeitet werden.');
        }

        const expiresAt = Date.now() + result.expiresIn * 1000;

        localStorage.setItem(TOKEN_KEY, result.idToken);
        localStorage.setItem(EXPIRY_KEY, String(expiresAt));
        localStorage.setItem(USER_KEY, JSON.stringify(userInfo));

        setToken(result.idToken);
        setUser(userInfo);

        return { success: true };
    }, []);

    /**
     * Logs out by clearing the stored token.
     */
    const logout = useCallback(() => {
        clearAuth();
    }, [clearAuth]);

    const isAuthenticated = !!token && !!user && !isTokenExpired(
        parseInt(localStorage.getItem(EXPIRY_KEY) || '0', 10)
    );

    const value = useMemo(() => ({
        user,
        token,
        login,
        logout,
        isAuthenticated,
        loading
    }), [user, token, login, logout, isAuthenticated, loading]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Hook to access the auth context.
 *
 * @returns {{ user: Object|null, token: string|null, login: Function, logout: Function, isAuthenticated: boolean, loading: boolean }}
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
